import { mapSlice } from '@atlaskit/editor-common/utils';
import type { Fragment, Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AgentManagedExtensionAttributes } from '../../common/agentManagedExtensionPluginType';
import {
	generateLocalId,
	getNodeAiManagedObject,
	isAgentManagedExtension,
} from '../../common/utils';
import { resolveCurrentPageUrl } from '../../services/resolvePageUrl';
import { archiveAiManagedObjectWithCache } from '../ai-managed-object-cache';

import {
	getAddedAndRemovedAgentManagedNodes,
	getAgentManagedNodeInfosFromSlice,
	getSelectedAgentManagedNodeSlice,
	unwrapPartiallySelectedNode,
} from './copy-paste-identity/document';
import {
	getAttrsWithCopyPasteMarker,
	getAttrsWithoutCopyPasteMarker,
	getCopyPasteMarker,
	getLocalId,
} from './copy-paste-identity/node-attrs';
import { processPastedNode, processReactivatedRemoval } from './copy-paste-identity/operations';
import { getCurrentReferenceObjectAri } from './copy-paste-identity/reference';
import type {
	ClipboardOperation,
	CopyPasteMarker,
	CreateCopyPasteIdentityArgs,
	PendingArchivedRemoval,
} from './copy-paste-identity/types';

type AgentManagedCopyPasteProps = {
	appendTransaction: (
		transactions: readonly Transaction[],
		oldState: EditorState,
		newState: EditorState,
	) => null;
	pendingArchivedRemovalsByLocalId: Map<string, PendingArchivedRemoval>;
	pendingPasteByLocalId: Map<string, CopyPasteMarker>;
	props: {
		handleDOMEvents: {
			copy: (view: EditorView) => boolean;
			cut: (view: EditorView) => boolean;
		};
		transformCopied: (slice: Slice) => Slice;
		transformPasted: (slice: Slice) => Slice;
	};
	readyArchiveLocalIds: Set<string>;
	readyPasteLocalIds: Set<string>;
	readyReactivationLocalIds: Set<string>;
};

type AgentManagedCopyPasteView = {
	update: (view: EditorView) => void;
};

/**
 * Creates the ProseMirror clipboard hooks and the transaction queues that coordinate
 * clipboard serialization with later document insert/remove transactions.
 */
export const createAgentManagedCopyPasteProps = (
	args: CreateCopyPasteIdentityArgs,
): AgentManagedCopyPasteProps => {
	let clipboardOperation: ClipboardOperation | undefined;
	let selectedAgentManagedNodeSlice: Slice | undefined;
	let clipboardContextId = 0;
	const pendingCutRemovalByLocalId = new Map<string, PendingArchivedRemoval>();
	const pendingArchivedRemovalsByLocalId = new Map<string, PendingArchivedRemoval>();
	const pendingPasteByLocalId = new Map<string, CopyPasteMarker>();
	const readyArchiveLocalIds = new Set<string>();
	const readyPasteLocalIds = new Set<string>();
	const readyReactivationLocalIds = new Set<string>();

	const markClipboardOperation = (
		operation: ClipboardOperation,
		view: EditorView,
	): Slice | undefined => {
		clipboardContextId += 1;
		const currentClipboardContextId = clipboardContextId;
		clipboardOperation = operation;
		selectedAgentManagedNodeSlice = getSelectedAgentManagedNodeSlice(view.state);

		// Keep transformCopied free of shared-state writes. ProseMirror serializes
		// clipboard content synchronously after the DOM event, then this context expires.
		queueMicrotask(() => {
			if (clipboardContextId === currentClipboardContextId) {
				clipboardOperation = undefined;
				selectedAgentManagedNodeSlice = undefined;
			}
		});

		return selectedAgentManagedNodeSlice;
	};

	return {
		appendTransaction: (
			transactions: readonly Transaction[],
			oldState: EditorState,
			newState: EditorState,
		): null => {
			const hasLocalDocumentChange = transactions.some(
				(tr) => tr.docChanged && !tr.getMeta('isRemote') && !tr.getMeta('replaceDocument'),
			);
			if (!hasLocalDocumentChange) {
				return null;
			}

			const { added, removed } = getAddedAndRemovedAgentManagedNodes(oldState, newState);

			removed.forEach(({ aiManagedObjectId, localId }) => {
				const pendingCutRemoval = pendingCutRemovalByLocalId.get(localId);
				const pendingRemoval =
					pendingCutRemoval ?? (aiManagedObjectId ? { aiManagedObjectId } : undefined);
				if (!pendingRemoval) {
					return;
				}

				// Cut intent is recorded by the DOM event before serialization, while
				// delete only becomes visible here. Queue the archive until the plugin view runs so
				// the cache can be updated through the live EditorView.
				pendingCutRemovalByLocalId.delete(localId);
				pendingArchivedRemovalsByLocalId.set(localId, pendingRemoval);
				readyArchiveLocalIds.add(localId);
			});

			added.forEach(({ localId }) => {
				if (pendingPasteByLocalId.has(localId)) {
					readyPasteLocalIds.add(localId);
					return;
				}

				if (pendingArchivedRemovalsByLocalId.has(localId)) {
					readyReactivationLocalIds.add(localId);
				}
			});

			return null;
		},
		pendingArchivedRemovalsByLocalId: pendingArchivedRemovalsByLocalId,
		pendingPasteByLocalId: pendingPasteByLocalId,
		props: {
			handleDOMEvents: {
				copy: (view: EditorView) => {
					pendingCutRemovalByLocalId.clear();
					markClipboardOperation('copy', view);
					return false;
				},
				cut: (view: EditorView) => {
					pendingCutRemovalByLocalId.clear();
					const selectedSlice = markClipboardOperation('cut', view);
					const cutSlice = selectedSlice ?? view.state.selection.content();

					// transformCopied may run multiple times for one cut. Record the cut
					// identities once, before serialization, and reconcile them after removal.
					getAgentManagedNodeInfosFromSlice(cutSlice).forEach(({ aiManagedObjectId, localId }) => {
						if (aiManagedObjectId) {
							pendingCutRemovalByLocalId.set(localId, {
								aiManagedObjectId,
							});
						}
					});
					return false;
				},
			},
			// The DOM event tells us whether ProseMirror is serializing for copy or cut;
			// transformCopied alone does not carry that intent.
			transformCopied: (slice: Slice): Slice => {
				const operation = clipboardOperation;
				const copiedSlice = selectedAgentManagedNodeSlice ?? slice;

				if (!operation) {
					return slice;
				}

				const sourcePageUrl = resolveCurrentPageUrl();
				return mapSlice(copiedSlice, (node: PMNode): PMNode | Fragment => {
					if (!isAgentManagedExtension(node)) {
						return node;
					}

					const completeNode = unwrapPartiallySelectedNode(copiedSlice, node);
					if (completeNode !== node) {
						return completeNode;
					}

					const attrs = node.attrs as AgentManagedExtensionAttributes;
					const aiManagedObject = getNodeAiManagedObject(attrs);
					if (!aiManagedObject?.id) {
						return node;
					}

					const marker: CopyPasteMarker = {
						operation,
						sourceAiManagedObjectId: aiManagedObject.id,
					};

					if (sourcePageUrl) {
						marker.sourcePageUrl = sourcePageUrl;
					}

					const sourceReferenceObjectAri = getCurrentReferenceObjectAri(args);
					if (sourceReferenceObjectAri) {
						marker.sourceReferenceObjectAri = sourceReferenceObjectAri;
					}

					// The marker exists only on the clipboard slice. Pasted content strips it and
					// queues async identity work in plugin state instead of storing transient data.
					return node.type.create(
						getAttrsWithCopyPasteMarker({
							attrs,
							clearAiManagedObject: operation === 'copy',
							marker,
							regenerateLocalId: operation === 'copy',
						}),
						node.content,
						node.marks,
					);
				});
			},
			// Paste strips transient clipboard metadata and queues follow-up AIMO work
			// until appendTransaction confirms which localId actually entered the document.
			transformPasted: (slice: Slice): Slice => {
				const currentReferenceObjectAri = getCurrentReferenceObjectAri(args);

				return mapSlice(slice, (node: PMNode): PMNode | Fragment => {
					if (!isAgentManagedExtension(node)) {
						return node;
					}

					const completeNode = unwrapPartiallySelectedNode(slice, node);
					if (completeNode !== node) {
						return completeNode;
					}

					const attrs = node.attrs as AgentManagedExtensionAttributes;
					const aiManagedObject = getNodeAiManagedObject(attrs);
					const marker =
						getCopyPasteMarker(attrs) ??
						(aiManagedObject?.id
							? {
									operation: 'copy' as const,
									sourceAiManagedObjectId: aiManagedObject.id,
								}
							: undefined);

					if (!marker) {
						return node;
					}

					const preserveCutIdentity =
						marker.operation === 'cut' &&
						(!marker.sourceReferenceObjectAri ||
							(Boolean(currentReferenceObjectAri) &&
								currentReferenceObjectAri === marker.sourceReferenceObjectAri));
					const nextLocalId = preserveCutIdentity
						? (getLocalId(attrs) ?? generateLocalId())
						: generateLocalId();
					pendingPasteByLocalId.set(nextLocalId, marker);

					return node.type.create(
						getAttrsWithoutCopyPasteMarker({
							attrs,
							clearAiManagedObject: !preserveCutIdentity,
							localId: nextLocalId,
						}),
						node.content,
						node.marks,
					);
				});
			},
		},
		readyArchiveLocalIds: readyArchiveLocalIds,
		readyPasteLocalIds: readyPasteLocalIds,
		readyReactivationLocalIds: readyReactivationLocalIds,
	};
};

/**
 * Runs queued async AIMO work from the plugin view so backend calls happen after
 * ProseMirror has committed the paste/cut/undo transaction.
 */
export const createAgentManagedCopyPasteView = (
	editorView: EditorView,
	args: CreateCopyPasteIdentityArgs & {
		pendingArchivedRemovalsByLocalId: Map<string, PendingArchivedRemoval>;
		pendingPasteByLocalId: Map<string, CopyPasteMarker>;
		readyArchiveLocalIds: Set<string>;
		readyPasteLocalIds: Set<string>;
		readyReactivationLocalIds: Set<string>;
	},
): AgentManagedCopyPasteView => {
	const inFlightLocalIds = new Set<string>();

	const processReadyArchives = (view: EditorView): void => {
		args.readyArchiveLocalIds.forEach((localId) => {
			const pendingRemoval = args.pendingArchivedRemovalsByLocalId.get(localId);
			if (!pendingRemoval || pendingRemoval.archivePromise) {
				return;
			}

			// Archive after ProseMirror commits the removal, then update the cached AIMO
			// status for toolbar and undo flows that still know this localId. Paste and
			// undo processors await this promise and short-circuit if archiving failed.
			args.readyArchiveLocalIds.delete(localId);
			const archivePromise = archiveAiManagedObjectWithCache(
				view,
				pendingRemoval.aiManagedObjectId,
				localId,
			).catch((error: unknown) => {
				const latestPendingRemoval =
					args.pendingArchivedRemovalsByLocalId.get(localId) ?? pendingRemoval;
				args.pendingArchivedRemovalsByLocalId.set(localId, {
					...latestPendingRemoval,
					archiveError: error,
					archiveFailed: true,
				});
			});
			args.pendingArchivedRemovalsByLocalId.set(localId, {
				...pendingRemoval,
				archivePromise,
			});
		});
	};

	const processReadyPastes = (view: EditorView): void => {
		args.readyPasteLocalIds.forEach((localId) => {
			const marker = args.pendingPasteByLocalId.get(localId);
			if (!marker || inFlightLocalIds.has(localId)) {
				return;
			}

			inFlightLocalIds.add(localId);
			args.readyPasteLocalIds.delete(localId);
			void processPastedNode({
				args,
				editorView: view,
				localId,
				marker,
				pendingArchivedRemovalsByLocalId: args.pendingArchivedRemovalsByLocalId,
			})
				.catch(() => undefined)
				.finally(() => {
					args.pendingPasteByLocalId.delete(localId);
					inFlightLocalIds.delete(localId);
				});
		});
	};

	const processReadyReactivations = (view: EditorView): void => {
		args.readyReactivationLocalIds.forEach((localId) => {
			if (args.pendingPasteByLocalId.has(localId) || inFlightLocalIds.has(localId)) {
				return;
			}

			inFlightLocalIds.add(localId);
			args.readyReactivationLocalIds.delete(localId);
			void processReactivatedRemoval({
				editorView: view,
				localId,
				pendingArchivedRemovalsByLocalId: args.pendingArchivedRemovalsByLocalId,
			})
				.catch(() => undefined)
				.finally(() => {
					inFlightLocalIds.delete(localId);
				});
		});
	};

	const update = (view: EditorView): void => {
		processReadyArchives(view);
		processReadyPastes(view);
		processReadyReactivations(view);
	};

	update(editorView);

	return {
		update: (view: EditorView): void => {
			update(view);
		},
	};
};
